export const SUPPORTED_LOCALES = ['en', 'ru'];
export const LOCALE_STORAGE_KEY = 'statusbot_locale';

export const MESSAGES = {
  en: {
    app: {
      brand: 'StatusBot',
    },
    locale: {
      en: 'English',
      ru: 'Русский',
    },
    nav: {
      dashboard: 'Dashboard',
      projects: 'Projects',
      account: 'Account',
      logout: 'Log out',
    },
    common: {
      loading: 'Loading…',
      language: 'Language',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving…',
      create: 'Create',
      creating: 'Creating…',
      edit: 'Edit',
      delete: 'Delete',
      open: 'Open',
      enabled: 'Enabled',
      disabled: 'Disabled',
      active: 'Active',
      paused: 'Paused',
      yes: 'Yes',
      no: 'No',
    },
    auth: {
      welcome: 'Welcome',
      getStarted: 'Get started',
      dashMySites: 'My sites',
      dashMonitored: 'Monitored',
      dashDown: 'Down',
      dashUptime30d: 'Uptime · 30 days',
      signIn: 'Sign in',
      createAccount: 'Create account',
      email: 'Email',
      password: 'Password',
      name: 'Name',
      passwordMin: 'Password (min 8 characters)',
      noAccount: 'No account?',
      alreadyHaveAccount: 'Already have an account?',
      register: 'Register',
      signInLink: 'Sign in',
      invalidCredentials: 'Invalid email or password',
      migrationNotice: 'On 15 October we moved StatusBot to a new platform, and accounts created before that date were not kept. Please',
      migrationNoticeLink: 'sign up again',
      migrationNoticeSorry: 'We apologise for the inconvenience.',
      connectionError: 'Connection error',
      signingIn: 'Signing in…',
      creatingAccount: 'Creating…',
      registrationFailed: 'Registration failed',
      emailTaken: 'This email is already registered.',
      autoLoginFailed: 'Could not sign in automatically after registration.',
    },
    verify: {
      title: 'E-mail confirmation',
      checking: 'Confirming your e-mail…',
      ok: 'Your e-mail has been confirmed. Thank you!',
      used: 'This link has already been used.',
      expired: 'This link has expired. Request a new one from your account.',
      invalid: 'This confirmation link is invalid.',
      noToken: 'No confirmation token provided.',
      toDashboard: 'Go to dashboard',
    },
    resetPassword: {
      title: 'Set a new password',
      newPassword: 'New password (min 8 characters)',
      confirm: 'Confirm new password',
      submit: 'Save password',
      done: 'Your password has been changed. You can sign in now.',
      toLogin: 'Go to sign in',
      tooShort: 'The password must be at least 8 characters.',
      mismatch: 'The passwords do not match.',
      failed: 'Could not reset the password. Please try again.',
      used: 'This reset link has already been used.',
      expired: 'This reset link has expired. Request a new one.',
      invalid: 'This reset link is invalid or has expired.',
    },
    verifyBanner: {
      text: 'Please confirm your e-mail to create projects and checks.',
      resend: 'Resend link',
      sending: 'Sending…',
      sent: 'Verification e-mail sent.',
      error: 'Could not send. Try again later.',
    },
    dashboard: {
      title: 'Dashboard',
      newProject: '+ New project',
      projects: 'Projects',
      active: 'Active',
      checksConfigured: 'Checks configured',
      yourProjects: 'Your projects',
      viewAll: 'View all',
      noProjectsYet: 'No projects yet.',
      createOne: 'Create one.',
      name: 'Name',
      status: 'Status',
      checks: 'Checks',
      created: 'Created',
      loading: 'Loading…',
    },
    projects: {
      title: 'Projects',
      intro: 'A project groups the checks and notifications for one team.',
      newProject: '+ New project',
      noProjectsYet: 'No projects yet. Create one to get started.',
      name: 'Name',
      status: 'Status',
      checks: 'Checks',
      created: 'Created',
      actions: 'Actions',
      deleteConfirm: 'Delete this project?',
      loading: 'Loading…',
    },
    projectModal: {
      title: 'New project',
      projectName: 'Project name',
      hint: 'A project groups a set of checks and notification settings for one team — its members monitor their services and get alerts together.',
      nameHelp: '3–64 characters, any language. Letters, digits, spaces and . _ - ; must start and end with a letter or digit.',
      failedToCreate: 'Failed to create project.',
      errors: {
        length: 'The name must be 3 to 64 characters long.',
        invalidChars: 'The name may contain letters, digits, spaces and . _ - , and must start and end with a letter or digit.',
        limit: 'Project limit reached — delete an existing project first.',
        taken: 'You already have a project with this name — choose another one.',
      },
    },
    account: {
      title: 'Account',
      profile: 'Profile',
      changePassword: 'Change password',
      saved: 'Saved.',
      failedToSave: 'Failed to save.',
      passwordsMismatch: 'New passwords do not match.',
      passwordChanged: 'Password changed.',
      failedToChangePassword: 'Failed to change password.',
      currentPassword: 'Current password',
      newPassword: 'New password (min 8 characters)',
      confirmNewPassword: 'Confirm new password',
      phone: 'Phone',
    },
    aiBot: {
      newTitle: 'New AI bot check',
      editTitle: 'Edit AI bot check',
      intro: 'StatusBot asks your support bot one question through its API and alerts you if it does not answer or answers against your instruction.',
      preset: 'Bot API type',
      presets: {
        http: 'Any HTTP API (JSON)',
        openai: 'OpenAI-compatible API (vLLM, LiteLLM, OpenRouter…)',
      },
      url: 'Bot API URL',
      baseUrl: 'API base URL',
      model: 'Model',
      bodyTemplate: 'Request body (JSON)',
      bodyTemplateHint: '{{question}} is replaced with the question, {{uuid}} with a new id on every run.',
      answerPath: 'Path to the answer in the response',
      authHeader: 'Key header',
      key: 'API key',
      keyHint: 'Sent in the key header. Stored encrypted; we recommend a separate key just for monitoring.',
      keyKeepHint: 'Leave empty to keep the saved key.',
      question: 'Question to the bot',
      questionPlaceholder: 'How do I return an item?',
      questionHint: 'It goes to your real bot: no personal data, please.',
      instruction: 'What a correct answer looks like',
      instructionPlaceholder: 'The bot names the 14-day return period and suggests filing a return in the account.',
      intervalHint: 'Each run is a real request to your bot (and a dialogue in its logs).',
      intervals: {
        hour1: 'Every hour',
        hour6: 'Every 6 hours',
        hour12: 'Every 12 hours',
        hour24: 'Once a day',
      },
      test: 'Check now',
      testing: 'Checking…',
      result: {
        ok: 'The bot answered correctly',
        answered: 'The bot answered',
        fail: 'The check would raise an alert',
        answer: 'Answer',
        reason: 'Why',
        notJudged: 'The answer was received but not checked against the instruction (the AI checker is not connected yet).',
      },
      errors: {
        limit: 'You already have an AI bot check — only one is available for now.',
        internal: 'This URL points to an internal network address.',
        disabled: 'AI bot checks are not available yet.',
        rateLimited: 'Please wait a few seconds before checking again.',
      },
    },
    projectDetail: {
      backToProjects: '← Projects',
      projectNotFound: 'Project not found.',
      rename: 'Rename',
      pause: 'Pause',
      resume: 'Resume',
      deleteProject: 'Delete project',
      deleteConfirm: 'Delete project "{{name}}"? This cannot be undone.',
      checks: 'Checks',
      notifications: 'Notifications',
      notificationsNotConfigured: 'Notifications not configured',
      setUp: 'Set up',
      addCheck: '+ Add check',
      renameFailed: 'Could not rename the project.',
      addAIBot: '+ AI bot',
      addNotification: '+ Add notification',
      noChecksYet: 'No checks yet. Add one to start monitoring.',
      noNotifications: 'No notifications configured.',
      url: 'URL',
      interval: 'Interval',
      protocols: 'Protocols',
      actions: 'Actions',
      method: 'Method',
      status: 'Status',
      editCheck: 'Edit check',
      newCheck: 'New check',
      editNotification: 'Edit notification',
      newNotification: 'New notification',
      editProject: 'Edit project',
      save: 'Save',
      loading: 'Loading…',
      deleteCheck: 'Delete this check?',
      deleteNotification: 'Delete this notification?',
      failedToSaveCheck: 'Failed to save check.',
      failedToSaveNotification: 'Failed to save notification.',
      invalidEmail: 'Enter a valid email address on a domain that can receive mail.',
      notifTest: {
        test: 'Test',
        sending: 'Sending…',
        sent: 'Test message sent.',
        failed: 'Test failed.',
      },
      telegram: {
        title: 'Telegram connection',
        intro: 'Connect a Telegram chat to receive alerts. For security the chat is bound only after you open the bot — you cannot enter a chat ID by hand.',
        connect: 'Connect Telegram',
        reconnect: 'Generate a new link',
        connecting: 'Generating link…',
        openInTelegram: 'Open in Telegram',
        addToGroup: 'Add to a group',
        orCommand: 'If the link does not open (e.g. blocked), send the bot this command:',
        waiting: 'Waiting for you to open the bot and press Start…',
        linkExpires: 'The link expires at {{time}}.',
        linked: 'Connected',
        linkedAs: 'Alerts go to: {{chat}}',
        unlink: 'Disconnect',
        unavailable: 'Telegram is not configured on the server.',
        statusNone: 'Not connected.',
        loadFailed: 'Failed to load Telegram status.',
      },
      domainPending: 'Domain pending',
      domainInvalid: 'Domain invalid',
      domainUnreachable: 'Domain unreachable',
      domain: 'Domain',
      // Short status-badge labels for the checks list (PIN-168): just the
      // protocol name, no "check" suffix.
      protoNames: {
        http: 'HTTP',
        ssl: 'SSL',
        domain: 'Domain',
        ping: 'Ping',
        ai_bot: 'AI bot',
        robots: 'robots.txt',
      },
      intervalOptions: {
        minute1: '1 minute',
        minute5: '5 minutes',
        minute15: '15 minutes',
        minute30: '30 minutes',
        hour1: '1 hour',
      },
      timeoutOptions: {
        second5: '5 seconds',
        second10: '10 seconds',
        second30: '30 seconds',
      },
      notifMethods: {
        logs: 'Logs (testing)',
        email: 'Email',
        telegram: 'Telegram',
        slack: 'Slack',
        webhook: 'Webhook',
        mattermost: 'Mattermost',
        matrix: 'Matrix',
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        soon: '(soon)',
      },
      checkFields: {
        websiteUrl: 'Website URL',
        urlPlaceholder: 'example.com or https://example.com',
        checkInterval: 'Check interval',
        httpCheck: 'HTTP check',
        enabled: 'Enabled',
        port: 'Port',
        expectedStatusCode: 'Expected status code',
        connectionTimeout: 'Connection timeout',
        sslCheck: 'SSL check',
        warnBeforeExpiry: 'Warn before expiry (days)',
        ignoreUntrustedCerts: 'Ignore untrusted certs',
        domainExpiryCheck: 'Domain expiry check',
        pingCheck: 'Ping check',
        robotsCheck: 'robots.txt check',
        robotsHint: 'Alerts if robots.txt is unavailable to search engines (5xx, no answer) or closes the whole site to Googlebot or Yandex.',
        maxDelay: 'Max delay (ms)',
        maxPacketLoss: 'Max packet loss (%)',
        emailAddress: 'Email address',
        botToken: 'Bot token',
        chatId: 'Chat ID',
        webhookUrl: 'Webhook URL',
        channelOptional: 'Channel (optional)',
        slackBotToken: 'Slack bot token (xoxb-…)',
        slackChannel: 'Channel (ID or #name)',
        hmacSecretOptional: 'HMAC secret (optional)',
        homeserverUrl: 'Homeserver URL',
        accessToken: 'Access token',
        roomId: 'Room ID',
        phoneNumber: 'Phone number',
      },
      history: {
        uptimeColumn: 'Uptime ({{days}}d)',
        incidents: '{{count}} incidents',
        noData: 'No history yet',
        period7: '7d',
        period30: '30d',
        period90: '90d',
      },
    },
  },
  ru: {
    app: {
      brand: 'StatusBot',
    },
    locale: {
      en: 'English',
      ru: 'Русский',
    },
    nav: {
      dashboard: 'Дашборд',
      projects: 'Проекты',
      account: 'Аккаунт',
      logout: 'Выйти',
    },
    common: {
      loading: 'Загрузка…',
      language: 'Язык',
      cancel: 'Отмена',
      save: 'Сохранить',
      saving: 'Сохранение…',
      create: 'Создать',
      creating: 'Создание…',
      edit: 'Редактировать',
      delete: 'Удалить',
      open: 'Открыть',
      enabled: 'Включено',
      disabled: 'Выключено',
      active: 'Активен',
      paused: 'Пауза',
      yes: 'Да',
      no: 'Нет',
    },
    auth: {
      welcome: 'Добро пожаловать',
      getStarted: 'Начнём',
      dashMySites: 'Мои сайты',
      dashMonitored: 'Под наблюдением',
      dashDown: 'Сбой',
      dashUptime30d: 'Аптайм · 30 дней',
      signIn: 'Войти',
      createAccount: 'Создать аккаунт',
      email: 'Email',
      password: 'Пароль',
      name: 'Имя',
      passwordMin: 'Пароль (минимум 8 символов)',
      noAccount: 'Нет аккаунта?',
      alreadyHaveAccount: 'Уже есть аккаунт?',
      register: 'Регистрация',
      signInLink: 'Войти',
      invalidCredentials: 'Неверный email или пароль',
      migrationNotice: '15 октября мы перенесли StatusBot на новую платформу, и аккаунты, созданные до этой даты, не сохранились. Пожалуйста,',
      migrationNoticeLink: 'зарегистрируйтесь заново',
      migrationNoticeSorry: 'Приносим извинения за неудобства.',
      connectionError: 'Ошибка соединения',
      signingIn: 'Входим…',
      creatingAccount: 'Создаём…',
      registrationFailed: 'Не удалось зарегистрироваться',
      emailTaken: 'Этот email уже зарегистрирован.',
      autoLoginFailed: 'Не удалось войти автоматически после регистрации.',
    },
    verify: {
      title: 'Подтверждение e-mail',
      checking: 'Подтверждаем ваш e-mail…',
      ok: 'Ваш e-mail подтверждён. Спасибо!',
      used: 'Эта ссылка уже была использована.',
      expired: 'Срок действия ссылки истёк. Запросите новую в личном кабинете.',
      invalid: 'Ссылка подтверждения недействительна.',
      noToken: 'Токен подтверждения не передан.',
      toDashboard: 'Перейти в дашборд',
    },
    resetPassword: {
      title: 'Новый пароль',
      newPassword: 'Новый пароль (минимум 8 символов)',
      confirm: 'Повторите пароль',
      submit: 'Сохранить пароль',
      done: 'Пароль изменён. Теперь можно войти.',
      toLogin: 'Перейти ко входу',
      tooShort: 'Пароль должен быть не короче 8 символов.',
      mismatch: 'Пароли не совпадают.',
      failed: 'Не удалось сменить пароль. Попробуйте ещё раз.',
      used: 'Эта ссылка для сброса уже была использована.',
      expired: 'Срок действия ссылки истёк. Запросите новую.',
      invalid: 'Ссылка для сброса недействительна или устарела.',
    },
    verifyBanner: {
      text: 'Подтвердите e-mail, чтобы создавать проекты и проверки.',
      resend: 'Отправить ссылку снова',
      sending: 'Отправляем…',
      sent: 'Письмо отправлено.',
      error: 'Не удалось отправить. Попробуйте позже.',
    },
    dashboard: {
      title: 'Дашборд',
      newProject: '+ Новый проект',
      projects: 'Проекты',
      active: 'Активные',
      checksConfigured: 'Проверок настроено',
      yourProjects: 'Ваши проекты',
      viewAll: 'Показать все',
      noProjectsYet: 'Проектов пока нет.',
      createOne: 'Создайте первый.',
      name: 'Имя',
      status: 'Статус',
      checks: 'Проверки',
      created: 'Создан',
      loading: 'Загрузка…',
    },
    projects: {
      title: 'Проекты',
      intro: 'Проект объединяет проверки и уведомления одной команды.',
      newProject: '+ Новый проект',
      noProjectsYet: 'Проектов пока нет. Создайте первый, чтобы начать.',
      name: 'Имя',
      status: 'Статус',
      checks: 'Проверки',
      created: 'Создан',
      actions: 'Действия',
      deleteConfirm: 'Удалить этот проект?',
      loading: 'Загрузка…',
    },
    projectModal: {
      title: 'Новый проект',
      projectName: 'Название проекта',
      hint: 'Проект объединяет набор проверок и настройки уведомлений для одной команды — её специалисты вместе следят за своими сервисами и получают оповещения.',
      nameHelp: '3–64 символа, любой язык. Буквы, цифры, пробелы и . _ - ; должно начинаться и заканчиваться буквой или цифрой.',
      failedToCreate: 'Не удалось создать проект.',
      errors: {
        length: 'Название должно содержать от 3 до 64 символов.',
        invalidChars: 'Название может содержать буквы, цифры, пробелы и . _ - , и должно начинаться и заканчиваться буквой или цифрой.',
        limit: 'Достигнут лимит проектов — сначала удалите существующий.',
        taken: 'У вас уже есть проект с таким названием — выберите другое.',
      },
    },
    account: {
      title: 'Аккаунт',
      profile: 'Профиль',
      changePassword: 'Сменить пароль',
      saved: 'Сохранено.',
      failedToSave: 'Не удалось сохранить.',
      passwordsMismatch: 'Новые пароли не совпадают.',
      passwordChanged: 'Пароль изменён.',
      failedToChangePassword: 'Не удалось сменить пароль.',
      currentPassword: 'Текущий пароль',
      newPassword: 'Новый пароль (минимум 8 символов)',
      confirmNewPassword: 'Подтвердите новый пароль',
      phone: 'Телефон',
    },
    aiBot: {
      newTitle: 'Новая проверка AI-бота',
      editTitle: 'Проверка AI-бота',
      intro: 'StatusBot задаёт вашему боту поддержки один вопрос через его API и сообщит, если бот не ответил или ответил не по вашей инструкции.',
      preset: 'Тип API бота',
      presets: {
        http: 'Произвольный HTTP API (JSON)',
        openai: 'OpenAI-совместимый API (vLLM, LiteLLM, OpenRouter…)',
      },
      url: 'URL API бота',
      baseUrl: 'Базовый URL API',
      model: 'Модель',
      bodyTemplate: 'Тело запроса (JSON)',
      bodyTemplateHint: '{{question}} заменится на вопрос, {{uuid}} — на новый идентификатор при каждом запуске.',
      answerPath: 'Путь к ответу в JSON',
      authHeader: 'Заголовок для ключа',
      key: 'API-ключ',
      keyHint: 'Передаётся в заголовке для ключа. Хранится зашифрованным; советуем завести отдельный ключ под мониторинг.',
      keyKeepHint: 'Оставьте пустым, чтобы сохранить текущий ключ.',
      question: 'Вопрос боту',
      questionPlaceholder: 'Как вернуть товар?',
      questionHint: 'Вопрос уходит вашему настоящему боту — без персональных данных.',
      instruction: 'Каким должен быть правильный ответ',
      instructionPlaceholder: 'Бот называет срок возврата 14 дней и предлагает оформить возврат в личном кабинете.',
      intervalHint: 'Каждый запуск — реальный запрос к вашему боту (и диалог в его логах).',
      intervals: {
        hour1: 'Раз в час',
        hour6: 'Раз в 6 часов',
        hour12: 'Раз в 12 часов',
        hour24: 'Раз в сутки',
      },
      test: 'Проверить сейчас',
      testing: 'Проверяем…',
      result: {
        ok: 'Бот ответил правильно',
        answered: 'Бот ответил',
        fail: 'Проверка подняла бы алерт',
        answer: 'Ответ',
        reason: 'Почему',
        notJudged: 'Ответ получен, но не сверен с инструкцией (AI-проверка ответа ещё не подключена).',
      },
      errors: {
        limit: 'У вас уже есть проверка AI-бота — пока доступна одна.',
        internal: 'Этот адрес ведёт во внутреннюю сеть.',
        disabled: 'Проверки AI-ботов пока недоступны.',
        rateLimited: 'Подождите несколько секунд перед повторной проверкой.',
      },
    },
    projectDetail: {
      backToProjects: '← Проекты',
      projectNotFound: 'Проект не найден.',
      rename: 'Переименовать',
      pause: 'Пауза',
      resume: 'Возобновить',
      deleteProject: 'Удалить проект',
      deleteConfirm: 'Удалить проект "{{name}}"? Это действие нельзя отменить.',
      checks: 'Проверки',
      notifications: 'Уведомления',
      notificationsNotConfigured: 'Уведомления не настроены',
      setUp: 'Настроить',
      addCheck: '+ Добавить проверку',
      renameFailed: 'Не удалось переименовать проект.',
      addAIBot: '+ AI-бот',
      addNotification: '+ Добавить уведомление',
      noChecksYet: 'Пока нет проверок. Добавьте первую, чтобы начать мониторинг.',
      noNotifications: 'Уведомления не настроены.',
      url: 'URL',
      interval: 'Интервал',
      protocols: 'Протоколы',
      actions: 'Действия',
      method: 'Метод',
      status: 'Статус',
      editCheck: 'Редактировать проверку',
      newCheck: 'Новая проверка',
      editNotification: 'Редактировать уведомление',
      newNotification: 'Новое уведомление',
      editProject: 'Редактировать проект',
      save: 'Сохранить',
      loading: 'Загрузка…',
      deleteCheck: 'Удалить эту проверку?',
      deleteNotification: 'Удалить это уведомление?',
      failedToSaveCheck: 'Не удалось сохранить проверку.',
      failedToSaveNotification: 'Не удалось сохранить уведомление.',
      invalidEmail: 'Укажите корректный адрес почты на домене, который может принимать письма.',
      notifTest: {
        test: 'Проверить',
        sending: 'Отправка…',
        sent: 'Тестовое сообщение отправлено.',
        failed: 'Проверка не удалась.',
      },
      telegram: {
        title: 'Подключение Telegram',
        intro: 'Подключите чат Telegram, чтобы получать уведомления. В целях безопасности чат привязывается только после того, как вы откроете бота — вписать chat ID вручную нельзя.',
        connect: 'Подключить Telegram',
        reconnect: 'Сгенерировать новую ссылку',
        connecting: 'Генерируем ссылку…',
        openInTelegram: 'Открыть в Telegram',
        addToGroup: 'Добавить в группу',
        orCommand: 'Если ссылка не открывается (например, заблокирована) — отправьте боту эту команду:',
        waiting: 'Ожидаем, пока вы откроете бота и нажмёте «Старт»…',
        linkExpires: 'Ссылка действует до {{time}}.',
        linked: 'Подключено',
        linkedAs: 'Уведомления идут в: {{chat}}',
        unlink: 'Отвязать',
        unavailable: 'Telegram не настроен на сервере.',
        statusNone: 'Не подключено.',
        loadFailed: 'Не удалось загрузить статус Telegram.',
      },
      domainPending: 'Домен ожидает проверки',
      domainInvalid: 'Некорректный домен',
      domainUnreachable: 'Домен недоступен',
      domain: 'Домен',
      protoNames: {
        http: 'HTTP',
        ssl: 'SSL',
        domain: 'Домен',
        ping: 'Ping',
        ai_bot: 'AI-бот',
        robots: 'robots.txt',
      },
      intervalOptions: {
        minute1: '1 минута',
        minute5: '5 минут',
        minute15: '15 минут',
        minute30: '30 минут',
        hour1: '1 час',
      },
      timeoutOptions: {
        second5: '5 секунд',
        second10: '10 секунд',
        second30: '30 секунд',
      },
      notifMethods: {
        logs: 'Логи (тест)',
        email: 'Email',
        telegram: 'Telegram',
        slack: 'Slack',
        webhook: 'Webhook',
        mattermost: 'Mattermost',
        matrix: 'Matrix',
        whatsapp: 'WhatsApp',
        sms: 'SMS',
        soon: '(скоро)',
      },
      checkFields: {
        websiteUrl: 'URL сайта',
        urlPlaceholder: 'example.com или https://example.com',
        checkInterval: 'Интервал проверки',
        httpCheck: 'Проверка HTTP',
        enabled: 'Включено',
        port: 'Порт',
        expectedStatusCode: 'Ожидаемый код ответа',
        connectionTimeout: 'Таймаут соединения',
        sslCheck: 'Проверка SSL',
        warnBeforeExpiry: 'Предупреждать до истечения (дни)',
        ignoreUntrustedCerts: 'Игнорировать недоверенные сертификаты',
        domainExpiryCheck: 'Проверка истечения домена',
        pingCheck: 'Проверка Ping',
        robotsCheck: 'Проверка robots.txt',
        robotsHint: 'Сообщит, если robots.txt недоступен поисковикам (5xx, нет ответа) или закрывает весь сайт от Googlebot или Яндекса.',
        maxDelay: 'Максимальная задержка (мс)',
        maxPacketLoss: 'Максимальная потеря пакетов (%)',
        emailAddress: 'Адрес email',
        botToken: 'Токен бота',
        chatId: 'ID чата',
        webhookUrl: 'Webhook URL',
        channelOptional: 'Канал (необязательно)',
        slackBotToken: 'Slack bot-токен (xoxb-…)',
        slackChannel: 'Канал (ID или #название)',
        hmacSecretOptional: 'HMAC secret (необязательно)',
        homeserverUrl: 'URL homeserver',
        accessToken: 'Токен доступа',
        roomId: 'Room ID',
        phoneNumber: 'Номер телефона',
      },
      history: {
        uptimeColumn: 'Аптайм ({{days}} дн.)',
        incidents: 'инцидентов: {{count}}',
        noData: 'Нет истории',
        period7: '7 дн.',
        period30: '30 дн.',
        period90: '90 дн.',
      },
    },
  },
};
